import { useState } from 'react';
import { allBusinessTags, allSourceTags } from '@/types/company';

interface BatchTagBarProps {
  selectedCount: number;
  onApplyTags: (businessTags: string[], sourceTags: string[]) => void;
  onClearSelection: () => void;
  onDelete: () => void;
}

export default function BatchTagBar({ selectedCount, onApplyTags, onClearSelection, onDelete }: BatchTagBarProps) {
  const [showPanel, setShowPanel] = useState(false);
  const [selectedBusinessTags, setSelectedBusinessTags] = useState<string[]>([]);
  const [selectedSourceTags, setSelectedSourceTags] = useState<string[]>([]);

  const toggleBusinessTag = (tag: string) => {
    setSelectedBusinessTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const toggleSourceTag = (tag: string) => {
    setSelectedSourceTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleApply = () => {
    if (selectedBusinessTags.length === 0 && selectedSourceTags.length === 0) return;
    onApplyTags(selectedBusinessTags, selectedSourceTags);
    setSelectedBusinessTags([]);
    setSelectedSourceTags([]);
    setShowPanel(false);
  };

  const handleCancel = () => {
    setSelectedBusinessTags([]);
    setSelectedSourceTags([]);
    setShowPanel(false);
  };

  return (
    <>
      {/* Floating bar */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background-50 rounded-2xl border border-background-200/70 px-4 py-3 flex items-center gap-4 shadow-sm max-w-[95vw]">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 text-sm font-bold">
            {selectedCount}
          </span>
          <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">已选择 {selectedCount} 家企业</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPanel(!showPanel)}
            className="flex items-center gap-1.5 px-3 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-price-tag-3-line text-sm"></i>
            批量打标签
          </button>
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-500 hover:bg-red-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-line text-sm"></i>
            批量删除
          </button>
          <button
            onClick={onClearSelection}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-foreground-500 hover:text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line text-sm"></i>
            取消选择
          </button>
        </div>
      </div>

      {/* Tag panel */}
      {showPanel && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={handleCancel}>
          <div className="absolute inset-0 bg-foreground-950/20" />
          <div
            className="relative bg-background-50 rounded-2xl w-full max-w-[500px] max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-5 py-3 flex items-center justify-between z-10">
              <div>
                <h3 className="text-sm font-heading font-bold text-foreground-900">批量打标签</h3>
                <p className="text-xs text-foreground-500">为选中的 {selectedCount} 家企业统一添加标签</p>
              </div>
              <button
                onClick={handleCancel}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Business tags */}
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-2">
                  业务标签
                  {selectedBusinessTags.length > 0 && (
                    <span className="text-accent-600 ml-1">（已选 {selectedBusinessTags.length} 个）</span>
                  )}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {allBusinessTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleBusinessTag(tag)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer border ${
                        selectedBusinessTags.includes(tag)
                          ? 'bg-accent-500 text-background-50 border-accent-500'
                          : 'bg-background-50 text-foreground-600 border-background-200 hover:border-accent-300 hover:text-accent-600'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Source tags */}
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-2">
                  来源标签
                  {selectedSourceTags.length > 0 && (
                    <span className="text-secondary-600 ml-1">（已选 {selectedSourceTags.length} 个）</span>
                  )}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {allSourceTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleSourceTag(tag)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer border ${
                        selectedSourceTags.includes(tag)
                          ? 'bg-secondary-500 text-background-50 border-secondary-500'
                          : 'bg-background-50 text-foreground-600 border-background-200 hover:border-secondary-300 hover:text-secondary-600'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-background-50 border-t border-background-200/70 px-5 py-3 flex items-center justify-end gap-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleApply}
                disabled={selectedBusinessTags.length === 0 && selectedSourceTags.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 bg-accent-500 hover:bg-accent-600 disabled:bg-accent-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line text-sm"></i>
                确认添加
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}